import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { Alert, Button, Menu, Modal, Table } from "@mantine/core";
import { bindActionCreators } from "redux";
import {
    addLoading,
    setPaysheetVisibilityStatus,
} from "../../../../data/actions/appointmentActions";
import {
    removeShiftVenueAdministrator,
    rosterShiftVenueAdministrator,
} from "../../../../data/actions/shiftActions";
import { IState } from "../../../../data/reducers/state";
import {
    IAdministratorAppointmentDetail,
    IAppointment,
    IAppointmentDetail,
} from "../../../../types/appointments/Appointment";
import { AppointmentStatus } from "../../../../types/appointments/AppointmentStatus";
import { BonusType } from "../../../../types/payrates/bonusType";
import { Role } from "../../../../types/persons/Role";
import { IPerson } from "../../../../types/persons/person";
import {
    IShift,
    IShiftAdmin,
    IShiftSession,
} from "../../../../types/shifts/Shift";
import { IVenue } from "../../../../types/venue/Venue";
import { toDateTime, toDateTimeNullable } from "../../../../util/date+helper";
import AppointmentView from "../../../appointments/AppointmentView";
import { CenteredLoader } from "../../../common/loader";
import TimeSelector from "../../../common/selectors/TimeSelector";
import AddLoadingView from "../AddLoadingView";
import { OfficialsSelector } from "../OfficialsSelector";

export interface IRosterAdministratorProps {
    shiftId: string;
    sessionId: string;
    venueId: string;

    shift?: IShift;
    session?: IShiftSession | null;

    showHideAppointmentFromSheetButton: boolean;

    personsMap?: { [personId: string]: IPerson };
    venueMap?: { [venueId: string]: IVenue };
    appointmentsMap?: {
        [appointmentId: string]: IAppointment<IAppointmentDetail>;
    };
    actions?: {
        // definitely filled, but optional for the ActivitiesPageProps extension
        setPaysheetVisibilityStatus: (
            appointmentId: string,
            show: boolean,
        ) => Promise<any>;
        rosterShiftVenueAdministrator: (
            sessionId: string,
            venueId: string,
            person: string,
            start: Date,
            finish: Date,
            usePersonalPayrate: boolean,
        ) => Promise<IShift>;
        removeShiftVenueAdministrator: (
            sessionId: string,
            venueId: string,
            appointmentId: string,
        ) => Promise<IShift>;

        addLoading: (appoinmentId: string, loading: any) => Promise<any>;
    };
}

export interface RosterAdministratorState {
    editingRow?: number | null;
    toPersist?: {
        person: string | null;
        start: string;
        finish: string;
        comment?: string;
        usePersonalPayrate: boolean
    } | null;
    persistSaving?: boolean;
    error?: string | null;
    openActionsRow?: number | null;
    addLoadingModalAppointment?: string | null;
    showAppointmentDetailsModalAppointment?: string | null;
}

class RosterAdministrator extends React.PureComponent<
    IRosterAdministratorProps,
    RosterAdministratorState
> {
    constructor(props, context) {
        super(props, context);

        this.state = {};

        this.getRow = this.getRow.bind(this);
        this.addAdministrator = this.addAdministrator.bind(this);
        this.persist = this.persist.bind(this);
        this.modifyAdministrator = this.modifyAdministrator.bind(this);
        this.toggleDropdown = this.toggleDropdown.bind(this);
        this.toggleAddLoading = this.toggleAddLoading.bind(this);
        this.toggleShowAppointment = this.toggleShowAppointment.bind(this);
    }

    venueTz() {
        return this.props.venueMap?.[this.props.venueId]?.tz;
    }

    correctedTz(date: DateTime) {
        const venueTz = this.venueTz();
        // just leave as current computer timzone
        if (!venueTz) return date
        return date.setZone(venueTz);
    }

    addAdministrator(start?: DateTime, finish?: DateTime) {
        let guessedStartDate: DateTime | undefined = start;
        let guessedFinishDate: DateTime | undefined = finish;

        const { session } = this.props;
        if (session) {
            const vid = this.props.venueId;
            if (!guessedFinishDate) {
                guessedFinishDate = toDateTime(session.finish, this.venueTz());
            }
            if (!guessedStartDate) {
                if (
                    !(
                        session.administrators &&
                        vid in session.administrators &&
                        session.administrators[vid].length > 0
                    )
                ) {
                    guessedStartDate = toDateTime(session.start, this.venueTz());
                } else {
                    const venueAdministrators = session.administrators[this.props.venueId];
                    const administrator = venueAdministrators[venueAdministrators.length - 1];
                    if (administrator && this.props.appointmentsMap) {
                        const appointment = this.props.appointmentsMap[
                            administrator.appointment
                        ] as IAppointment<IAdministratorAppointmentDetail>;
                        if (appointment && toDateTime(appointment.finish) < toDateTime(session.finish)) {
                            guessedStartDate = toDateTime(appointment.finish, this.venueTz());
                            // guessedFinishDate = new Date(appointment.details.finish)
                        }
                    }
                }
            }
        }

        this.setState({
            toPersist: {
                person: null,
                start: (guessedStartDate || DateTime.now()).toISO()!,
                finish: (guessedFinishDate || DateTime.now()).toISO()!,
                comment: "",
                usePersonalPayrate: true,
            },
        });
    }

    getRowAction(
        action: string | Array<string>,
        person: string | null,
        appointment: IAppointment<IAdministratorAppointmentDetail> | undefined,
        rowIndex: number,
    ) {
        if (action instanceof Array)
            return action.map((m) =>
                this.getRowAction(m, person, appointment, rowIndex),
            );

        const appointmentIsLocked =
            !!appointment && AppointmentStatus.isLocked(appointment.status);

        const key = `action-${action}-${person}-${rowIndex}`;

        switch (action) {
            case "add":
                return (
                    <Button key={key} color="blue" variant="outline" onClick={this.persist}>
                        Save
                    </Button>
                );
            case "loading":
                return <CenteredLoader key={key} />;
            case "x":
                return person ? (
                    <Menu
                        key={key}
                        opened={this.state.openActionsRow == rowIndex}
                        onClose={() => this.toggleDropdown(undefined)}
                        onOpen={() => this.toggleDropdown(rowIndex)}
                    >
                        <Menu.Target>
                            <Button color="gray" style={{ color: "grey" }}>
                                <FontAwesomeIcon icon={faEllipsisV} />
                            </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                            {!appointmentIsLocked && (
                                <Menu.Item
                                    onClick={() => this.removeAdministrator(appointment!._id!)}
                                    style={{ color: "#dc3545", height: "4vh" }}
                                >
                                    Remove {this.props.personsMap?.[person]?.firstName}
                                </Menu.Item>
                            )}

                            {appointment && (
                                <>
                                    {!appointmentIsLocked && (
                                        <>
                                            <Menu.Item
                                                onClick={() => this.toggleAddLoading(appointment?._id)}
                                                style={{ height: "4vh" }}
                                                disabled={appointmentIsLocked}
                                            >
                                                Add loading
                                            </Menu.Item>

                                            <Menu.Item
                                                onClick={() =>
                                                    this.addZeroPaymentLoading(appointment?._id)
                                                }
                                            >
                                                Set payment to $0.00
                                            </Menu.Item>
                                            {this.props.showHideAppointmentFromSheetButton && <Menu.Item
                                                onClick={() => {
                                                    this.props?.actions?.setPaysheetVisibilityStatus(
                                                        appointment._id,
                                                        appointment?.payment?.showTotal === false,
                                                    );
                                                }}
                                            >
                                                {appointment?.payment?.showTotal === false
                                                    ? "Show"
                                                    : "Hide"}{" "}
                                                appointment total on paysheet
                                            </Menu.Item>}
                                        </>
                                    )}
                                    <Menu.Item
                                        onClick={() => this.toggleShowAppointment(appointment?._id)}
                                        style={{ height: "4vh" }}
                                    >
                                        Show details
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Label>
                                        {appointmentIsLocked && "Appointment is locked as it was "}
                                        {AppointmentStatus.toString(appointment.status)} on{" "}
                                        {toDateTimeNullable(
                                            appointment.statuses.filter(
                                                (s) => s.status == appointment.status,
                                            )[0]?.date,
                                        )?.toLocaleString(DateTime.DATETIME_FULL)}
                                    </Menu.Label>
                                    {appointment?.payment && (
                                        <Menu.Label>
                                            Total: ${Number(appointment.payment.total).toFixed(2)}
                                        </Menu.Label>
                                    )}
                                </>
                            )}
                        </Menu.Dropdown>
                    </Menu>
                ) : (
                    <Button
                        key={key}
                        variant="outline"
                        onClick={() =>
                            this.setState((state) => ({ ...state, toPersist: null }))
                        }
                        color="red"
                        size="sm"
                    >
                        X
                    </Button>
                );
        }
        return action;
    }

    getRow(
        rowIndex: number,
        person: string | null,
        start: DateTime,
        finish: DateTime,
        mode,
        canModifyPerson: boolean,
        appointment?: IAppointment<IAdministratorAppointmentDetail>,
    ) {
        const modeAction = this.getRowAction(mode, person, appointment, rowIndex);
        const appointmentIsLocked =
            appointment && AppointmentStatus.isLocked(appointment.status);
        const defaultRowStyle = {
            color: appointmentIsLocked ? "var(--color-text-disabled)" : undefined,
        }
        const editPersonAction = canModifyPerson ? (
            <OfficialsSelector
                name="person"
                person={person}
                role={Role.administrative}
                shiftId={this.props.shiftId}
                onChange={this.modifyAdministrator}
            />
        ) : (
            <span
                style={{
                    // backgroundColor: appointmentIsLocked ? "#e9ecef" : "",
                    ...defaultRowStyle,
                    padding: "10px",
                }}
            >
                {this.props.personsMap &&
                    person &&
                    person in this.props.personsMap &&
                    this.props.personsMap[person].fullName}
            </span>
        );

        let minTime: DateTime | null = null;
        let maxTime: DateTime | null = null;

        const range = this.props.session || { start: null, finish: null };
        minTime = range.start && toDateTime(range.start, this.venueTz()).minus({ minutes: 60 });
        maxTime = range.finish && toDateTime(range.finish, this.venueTz()).plus({ minutes: 60 });

        return (
            <tr key={(person || "unrostered") + start}>
                <td>{editPersonAction}</td>
                <td style={defaultRowStyle}>
                    {canModifyPerson ? (
                        <TimeSelector
                            name="start"
                            tz={this.venueTz()}
                            time={start}
                            minTime={minTime}
                            maxTime={finish}
                            onChange={this.modifyAdministrator}
                        />
                    ) : (
                        this.correctedTz(start).toLocaleString(DateTime.TIME_SIMPLE)
                    )}
                </td>
                <td style={defaultRowStyle}>
                    {canModifyPerson ? (
                        <TimeSelector
                            name="finish"
                            tz={this.venueTz()}
                            time={finish}
                            minTime={start}
                            maxTime={maxTime}
                            onChange={this.modifyAdministrator}
                        />
                    ) : (
                        this.correctedTz(finish).toLocaleString(DateTime.TIME_SIMPLE)
                    )}
                </td>
                <td>{modeAction}</td>
            </tr>
        );
    }

    getRowFromAppointment(
        rowIndex: number,
        administrator: IShiftAdmin,
        mode,
        canModifyPerson: boolean,
    ) {
        const appointment =
            this.props.appointmentsMap &&
            (this.props.appointmentsMap[
                administrator.appointment
            ] as IAppointment<IAdministratorAppointmentDetail>);
        const s = appointment?.start;
        const f = appointment?.finish;

        if (!s || !f) {
            return <tr><td>Invalid appointment</td></tr>
        }

        return this.getRow(
            rowIndex,
            administrator.person,
            toDateTime(s, this.venueTz()),
            toDateTime(f, this.venueTz()),
            mode,
            canModifyPerson,
            appointment,
        );
    }

    persist() {
        if (!this.state.toPersist || !this.props.actions)
            return alert("An internal error has ocurred");
        if (!this.state.toPersist.person)
            return alert("Must add person before saving");

        this.setState((state) => ({ ...state, persistSaving: true }));
        this.props.actions
            .rosterShiftVenueAdministrator(
                this.props.sessionId,
                this.props.venueId,
                this.state.toPersist.person,
                new Date(this.state.toPersist.start),
                new Date(this.state.toPersist.finish),
                this.state.toPersist.usePersonalPayrate,
            )
            .then(() => {

                this.setState((state) => ({
                    ...state,
                    toPersist: null,
                    persistSaving: false,
                }))
            })
            .catch((er) => {

                this.setState((state) => ({
                    ...state,
                    persistSaving: false,
                    error: er.message,
                }))
            });
    }

    removeAdministrator(appointmentId: string | null) {
        if (!appointmentId || !this.props.actions) return;
        this.props.actions.removeShiftVenueAdministrator(
            this.props.sessionId,
            this.props.venueId,
            appointmentId,
        ).then;
    }

    modifyAdministrator(name: string, value: any) {
        if (!this.state.toPersist) return;
        // @ts-ignore

        const dateValue = toDateTimeNullable(value);
        if (dateValue) {
            value = dateValue;
        }

        // @ts-ignore
        this.setState((state) => ({
            ...state,
            toPersist: { ...this.state.toPersist, [name]: value },
        }));
    }

    render() {
        const { shift, venueId, session } = this.props;
        const { error } = this.state;

        if (!shift) return <p>Couldn't find shift.</p>;
        if (!session) return <p>Couldn't find session</p>;

        const administrators = session.administrators || {};
        const venueAdministrators: IShiftAdmin[] = administrators[venueId] || [];

        let addAdministratorButton: React.ReactElement = (
            <Button color="blue" variant="outline" onClick={() => this.addAdministrator()}>
                Add administrator (+)
            </Button>
        );

        return (
            <>
                <Table style={{ width: "fit-content" }}>
                    <thead>
                        <tr>
                            <td style={{ minWidth: "300px" }}>Administrator</td>
                            <td>Start</td>
                            <td>Finish</td>
                            <td style={{ minWidth: "fit-content" }}>Mode</td>
                        </tr>
                    </thead>
                    <tbody>
                        {venueAdministrators.map((vs, idx) =>
                            this.getRowFromAppointment(idx, vs, "x", false),
                        )}
                        {this.state.toPersist &&
                            this.getRow(
                                venueAdministrators.length + 1,
                                this.state.toPersist.person,
                                toDateTime(this.state.toPersist.start, this.venueTz()),
                                toDateTime(this.state.toPersist.finish, this.venueTz()),
                                this.state.persistSaving
                                    ? "loading"
                                    : this.state.toPersist.person
                                        ? ["add", "x"]
                                        : "x",
                                true,
                            )}
                    </tbody>
                    { }
                </Table>
                {error && (
                    <Alert color="red" withCloseButton onClose={() => this.setState({ error: null })}>
                        {error}
                    </Alert>
                )}
                {!this.state.toPersist && (
                    <div style={{ width: "fit-content", margin: "auto" }}>
                        {addAdministratorButton}
                    </div>
                )}
                {this.state.showAppointmentDetailsModalAppointment && (
                    <Modal
                        opened={!!this.state.showAppointmentDetailsModalAppointment}
                        onClose={() => this.toggleShowAppointment(null)}
                        title="Appointment"
                    >
                        <AppointmentView
                            appointmentId={
                                this.state.showAppointmentDetailsModalAppointment
                            }
                            disableEdits={false}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                            <Button
                                color="gray"
                                onClick={() => this.toggleShowAppointment(null)}
                            >
                                Close
                            </Button>
                        </div>
                    </Modal>
                )}
                {this.state.addLoadingModalAppointment && (
                    <Modal
                        opened={Boolean(this.state.addLoadingModalAppointment)}
                        onClose={() => this.toggleAddLoading(null)}
                        size="sm"
                        title="Add loading"
                    >
                        <AddLoadingView
                            toggle={() => this.toggleAddLoading(null)}
                            appointmentId={this.state.addLoadingModalAppointment}
                        />
                    </Modal>
                )}
            </>
        );
    }

    toggleDropdown(rowIndex?: number) {
        const newRowIndex = typeof rowIndex === "number" ? rowIndex : null;
        this.setState({ openActionsRow: newRowIndex });
    }

    toggleAddLoading(appointmentId: string | undefined | null) {
        this.setState({ addLoadingModalAppointment: appointmentId });
    }

    toggleShowAppointment(appointmentId: string | undefined | null) {
        this.setState({ showAppointmentDetailsModalAppointment: appointmentId });
    }

    addZeroPaymentLoading(appointmentId) {
        if (!appointmentId) {
            return console.log(
                "Couldn't add zero payment loading as an internal error occurred",
            );
        }

        this.props.actions
            ?.addLoading(appointmentId, { reason: BonusType.zeroPayment, value: 0 })
            .then((resp) => {
                console.log(resp);
            })
            .catch((er) => {
                alert(er.message);
                console.error(er);
            });
    }
}

function mapStateToProps(state: IState, props) {
    const shift = state.shifts.map[props.shiftId];

    if (!shift || !props.sessionId) return { shift, session: null };
    const filledShift = state.shiftSessions.filled[props.sessionId];
    return {
        shift,
        session: filledShift && filledShift.session,
        personsMap: state.persons.map,
        appointmentsMap: state.appointments.map,
        venueMap: state.venues.map,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(
            {
                rosterShiftVenueAdministrator,
                removeShiftVenueAdministrator,
                addLoading,
                setPaysheetVisibilityStatus,
            },
            dispatch,
        ),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(RosterAdministrator);
